import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useWorkflow, type WorkflowOverview } from "../../api/workflow";
import { PageHead } from "../../components/ui";
import "../../styles/workflow.css";
import { JourneyCard, NoJourney } from "./JourneyCard";
import { ownerLabel } from "./labels";
import { OverdueCard } from "./OverdueCard";
import { StageStrip } from "./StageStrip";

/** Where every job stands against its SLA: the stages as a strip, the overdue
 * queue worst-first, and one job's journey with its recorded milestones.
 * `/workflow/sites/:siteId` picks the job; without one, the worst overdue job
 * is shown, because that is the one somebody opening this wants to chase. */
export default function WorkflowPage() {
  const { siteId } = useParams();
  const view = useWorkflow();
  if (view.isPending) {
    return <p className="app-loading">Loading the workflow…</p>;
  }
  if (view.isError) {
    return <p className="notice bad">The workflow could not be loaded: {view.error.message}</p>;
  }
  const asNumber = Number(siteId);
  const picked = siteId !== undefined && Number.isInteger(asNumber) ? asNumber : null;
  return (
    <WorkflowView
      data={view.data}
      selectedId={picked}
      journey={(id) => (id === null ? <NoJourney /> : <JourneyCard key={id} siteId={id} />)}
    />
  );
}

/** Every owner the stages name, once each, in stage order. */
export function ownersOf(data: WorkflowOverview): string[] {
  return [...new Set(data.stages.map((s) => s.owner))];
}

export function WorkflowView({
  data,
  selectedId,
  journey,
}: {
  data: WorkflowOverview;
  selectedId: number | null;
  /** The journey panel for a site, or for none; a slot so the view renders
   * without the network. */
  journey: (siteId: number | null) => ReactNode;
}) {
  const shown = selectedId ?? data.overdue[0]?.site.id ?? null;
  const withOwners = data.by_owner.map((o) => `${ownerLabel(o.owner)} ${o.jobs}`).join(" · ");
  return (
    <div className="workflow-page">
      <PageHead
        title="Workflow"
        sub="Every job against its SLA"
        actions={
          <p className="workflow-totals" aria-label="Totals">
            <span>
              <strong>{data.total}</strong> jobs
            </span>
            <span title={withOwners === "" ? undefined : `With ${withOwners}`}>
              <strong>{data.in_flight}</strong> in flight
            </span>
            <span>
              <strong>{data.complete}</strong> complete
            </span>
            <span className={data.overdue_count > 0 ? "late" : undefined}>
              <strong>{data.overdue_count}</strong> overdue
            </span>
          </p>
        }
      />
      <StageStrip stages={data.stages} />
      {data.gaps.length > 0 ? (
        // Not lateness: the work went past these stages and nobody wrote them
        // down. A different problem, for a different person, so it is said
        // apart from the overdue queue rather than counted in it.
        <p className="notice warn workflow-gaps">
          Passed without a record:{" "}
          {data.gaps.map((g) => `${g.stage} (${g.jobs} job${g.jobs === 1 ? "" : "s"})`).join(", ")}.
        </p>
      ) : null}
      <div className="workflow-split">
        <OverdueCard rows={data.overdue} owners={ownersOf(data)} selectedId={shown} />
        {journey(shown)}
      </div>
    </div>
  );
}
